import { observable, runInAction } from 'mobx'

import { IProject, SitePermitStatus } from '~/client/graph'
import { CompaniesByProjectIdDocument } from '~/client/graph/operations/generated/Companies.generated'
import { LocationsByProjectIdDocument } from '~/client/graph/operations/generated/LocationType.generated'
import { PermitTypeListDocument } from '~/client/graph/operations/generated/PermitTypes.generated'
import { GetSitePermitAggregationListDocument } from '~/client/graph/operations/generated/SitePermits.generated'
import Workflow from '~/client/src/shared/models/Workflow'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import GraphExecutorStore from '~/client/src/shared/stores/domain/GraphExecutor.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import ProjectsStore from '~/client/src/shared/stores/domain/Projects.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import InitialState from '~/client/src/shared/stores/InitialState'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import { IWorkflowLinkRef } from './workflowLinks'

export interface IWorkflowCardLocation {
  name: string
  color: string
  iconName: string
}

export interface IWorkflowCardData {
  typeName: string
  typeIconName: string
  caption: string
  status: SitePermitStatus
  workflowStepLevel: number
  isLate: boolean
  companyNames: string[]
  dateText: string
  locations: IWorkflowCardLocation[]
  projectId: string
  projectCode: string
  projectName: string
  isActiveProject: boolean
}

export type IWorkflowCardResult =
  | { state: 'pending' | 'absent' }
  | { state: 'ready'; card: IWorkflowCardData }

type ICachedCard = Omit<IWorkflowCardData, 'isActiveProject'>

type ICachedResult = { state: 'absent' } | { state: 'ready'; card: ICachedCard }

interface IProjectContext {
  getTypeById(typeId: string): { name?: string; iconName?: string } | undefined
  getCompanyNameById(companyId: string): string | undefined
  getLocationById(
    locationId: string,
  ): { name?: string; color?: string; iconName?: string } | undefined
}

interface IWorkflowCardSources extends IProjectContext {
  workflow: Workflow
  project: IProject
  formatDateInterval(startDate: number, endDate: number): string
}

type ICardOutcome =
  | { status: 'ready'; card: ICachedCard }
  | { status: 'absent' }
  | { status: 'error' }

const pendingResult: IWorkflowCardResult = { state: 'pending' }
const absentResult: ICachedResult = { state: 'absent' }
const absentOutcome: ICardOutcome = { status: 'absent' }
const errorOutcome: ICardOutcome = { status: 'error' }

const MAX_LOAD_ATTEMPTS = 3
const RETRY_DELAYS_MS = [5000, 15000]

export function getWorkflowCaption(
  code: string,
  title: string,
  typeName: string,
): string {
  const codePrefix = code ? `${code}: ` : ''
  return codePrefix + (title || typeName || '')
}

export function composeWorkflowCard(
  sources: IWorkflowCardSources,
): ICachedCard {
  const {
    workflow,
    project,
    getTypeById,
    getCompanyNameById,
    getLocationById,
    formatDateInterval,
  } = sources

  const workflowType = getTypeById(workflow.typeId)

  return {
    typeName: workflowType?.name || '',
    typeIconName: workflowType?.iconName || '',
    caption: getWorkflowCaption(
      workflow.code,
      workflow.title,
      workflowType?.name,
    ),
    status: workflow.status,
    workflowStepLevel: workflow.workflowStepLevel,
    isLate: workflow.isLate,
    companyNames: workflow.companyIds
      .map(getCompanyNameById)
      .filter(name => !!name),
    dateText: formatDateInterval(workflow.startDate, workflow.endDate),
    locations: workflow.locations
      .map(location => getLocationById(location.id))
      .filter(location => !!location)
      .map(location => ({
        name: location.name || '',
        color: location.color || '',
        iconName: location.iconName || '',
      })),
    projectId: project.id,
    projectCode: project.code,
    projectName: project.name,
  }
}

export default class WorkflowCardDataStore {
  private readonly results = observable(new Map<string, ICachedResult>())
  private readonly loadingKeys = new Set<string>()
  private readonly projectContexts = new Map<string, Promise<IProjectContext>>()
  private readonly attemptsByKey = new Map<string, number>()
  private readonly retryTimers = new Set<ReturnType<typeof setTimeout>>()
  private isDisposed = false

  public constructor(
    private readonly state: InitialState,
    private readonly projectsStore: ProjectsStore,
    private readonly graphExecutorStore: GraphExecutorStore,
    private readonly projectDateStore: ProjectDateStore,
    private readonly permitTypesStore: WorkflowTypesStore,
    private readonly companiesStore: CompaniesStore,
    private readonly locationAttributesStore: LocationAttributesStore,
  ) {}

  public peekCard = (link: IWorkflowLinkRef): IWorkflowCardResult => {
    const cached = this.results.get(this.getKey(link))

    if (!cached) {
      return pendingResult
    }

    if (cached.state === 'absent') {
      return cached
    }

    return {
      state: 'ready',
      card: {
        ...cached.card,
        isActiveProject: cached.card.projectId === this.state.activeProject?.id,
      },
    }
  }

  public loadCards = (links: IWorkflowLinkRef[]) => {
    const freshLinksByCode = new Map<string, IWorkflowLinkRef[]>()

    links.forEach(link => {
      const key = this.getKey(link)

      if (this.results.has(key) || this.loadingKeys.has(key)) {
        return
      }

      this.loadingKeys.add(key)
      const group = freshLinksByCode.get(link.projectCode) || []
      group.push(link)
      freshLinksByCode.set(link.projectCode, group)
    })

    freshLinksByCode.forEach((groupLinks, projectCode) =>
      this.loadProjectGroup(projectCode, groupLinks),
    )
  }

  public dispose = () => {
    this.isDisposed = true
    this.retryTimers.forEach(clearTimeout)
    this.retryTimers.clear()
  }

  private getKey(link: IWorkflowLinkRef): string {
    return `${link.projectCode}:${link.workflowId}`
  }

  private async loadProjectGroup(
    projectCode: string,
    links: IWorkflowLinkRef[],
  ): Promise<void> {
    const outcomes = await this.resolveGroup(projectCode, links).catch(() =>
      links.map(() => errorOutcome),
    )

    links.forEach(link => this.loadingKeys.delete(this.getKey(link)))

    if (this.isDisposed) {
      return
    }

    const failedLinks: IWorkflowLinkRef[] = []

    runInAction(() => {
      links.forEach((link, index) => {
        const key = this.getKey(link)
        const outcome = outcomes[index]

        if (outcome.status === 'error') {
          failedLinks.push(link)
          return
        }

        this.attemptsByKey.delete(key)
        this.results.set(
          key,
          outcome.status === 'ready'
            ? { state: 'ready', card: outcome.card }
            : absentResult,
        )
      })
    })

    this.scheduleRetry(failedLinks)
  }

  private scheduleRetry(failedLinks: IWorkflowLinkRef[]): void {
    let groupAttempts = 0

    const retryLinks = failedLinks.filter(link => {
      const key = this.getKey(link)
      const attempts = (this.attemptsByKey.get(key) || 0) + 1
      this.attemptsByKey.set(key, attempts)
      groupAttempts = Math.max(groupAttempts, attempts)
      return attempts < MAX_LOAD_ATTEMPTS
    })

    if (!retryLinks.length) {
      return
    }

    const delay =
      RETRY_DELAYS_MS[Math.min(groupAttempts, RETRY_DELAYS_MS.length) - 1]

    const timer = setTimeout(() => {
      this.retryTimers.delete(timer)
      this.loadCards(retryLinks)
    }, delay)

    this.retryTimers.add(timer)
  }

  private async resolveGroup(
    projectCode: string,
    links: IWorkflowLinkRef[],
  ): Promise<ICardOutcome[]> {
    const project = this.projectsStore.getProjectByCode(projectCode)
    if (!project?.id) {
      return links.map(() => absentOutcome)
    }

    const ids = links.map(link => link.workflowId)

    const [permitResult, context] = await Promise.all([
      this.graphExecutorStore.query(GetSitePermitAggregationListDocument, {
        filter: { projectId: project.id, ids },
        limit: ids.length,
        offset: 0,
      }),
      this.getProjectContext(project),
    ])

    if (permitResult.error || !context) {
      return links.map(() => errorOutcome)
    }

    const dtoById = new Map(
      (permitResult.data?.sitePermitsAggregation?.data || []).map(dto => [
        dto.id,
        dto,
      ]),
    )

    return links.map(link => {
      const dto = dtoById.get(link.workflowId)
      if (!dto) {
        return absentOutcome
      }

      const workflow = Workflow.fromDto(dto)
      if (workflow.isDeleted) {
        return absentOutcome
      }

      return {
        status: 'ready',
        card: composeWorkflowCard({
          ...context,
          workflow,
          project,
          formatDateInterval: this.projectDateStore.getCompactDateTimeInterval,
        }),
      }
    })
  }

  private getProjectContext(project: IProject): Promise<IProjectContext> {
    if (this.canUseActiveProjectStores(project.id)) {
      return Promise.resolve(this.snapshotActiveProjectContext())
    }

    if (!this.projectContexts.has(project.id)) {
      this.projectContexts.set(
        project.id,
        this.queryProjectContext(project.id).then(context => {
          if (!context) {
            this.projectContexts.delete(project.id)
          }
          return context
        }),
      )
    }

    return this.projectContexts.get(project.id)
  }

  private canUseActiveProjectStores(projectId: string): boolean {
    return (
      projectId === this.state.activeProject?.id &&
      this.permitTypesStore.isDataReceived &&
      this.companiesStore.isDataReceived &&
      this.locationAttributesStore.isDataReceived
    )
  }

  private snapshotActiveProjectContext(): IProjectContext {
    const typesById = new Map<string, { name: string; iconName: string }>()
    this.permitTypesStore.allTypes.forEach(type => {
      if (!typesById.has(type.id)) {
        typesById.set(type.id, { name: type.name, iconName: type.iconName })
      }
    })

    const companyNamesById = new Map(
      this.companiesStore.allCompanies.map(company => [
        company.id,
        company.name,
      ]),
    )

    const locationsById = new Map(
      Object.entries(
        this.locationAttributesStore.hierarchyAllAttributesMap,
      ).map(([id, location]) => [
        id,
        {
          name: location.name,
          color: location.color,
          iconName: location.iconName,
        },
      ]),
    )

    return {
      getTypeById: typeId => typesById.get(typeId),
      getCompanyNameById: companyId => companyNamesById.get(companyId),
      getLocationById: locationId => locationsById.get(locationId),
    }
  }

  private async queryProjectContext(
    projectId: string,
  ): Promise<IProjectContext> {
    const [typesResult, companiesResult, locationsResult] = await Promise.all([
      this.graphExecutorStore.query(PermitTypeListDocument, { projectId }),
      this.graphExecutorStore.query(CompaniesByProjectIdDocument, {
        projectId,
      }),
      this.graphExecutorStore.query(LocationsByProjectIdDocument, {
        projectId,
      }),
    ])

    if (typesResult.error || companiesResult.error || locationsResult.error) {
      return null
    }

    const typesById = new Map(
      (typesResult.data?.permitTypes?.data || []).map(type => [type.id, type]),
    )
    const companyNamesById = new Map(
      (companiesResult.data?.companies?.data || []).map(company => [
        company.id,
        company.name,
      ]),
    )
    const locationsById = new Map(
      (locationsResult.data?.locations?.data || []).map(location => [
        location.id,
        location,
      ]),
    )

    return {
      getTypeById: typeId => typesById.get(typeId),
      getCompanyNameById: companyId => companyNamesById.get(companyId),
      getLocationById: locationId => locationsById.get(locationId),
    }
  }
}
